// CustomDropdownThana.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    View,
    Text,
    TouchableOpacity,
    FlatList,
    TextInput,
    StyleSheet,
    Dimensions,
    Platform,
    Pressable
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
const { width: windowWidth, height: windowHeight } = Dimensions.get('window');
const IS_WEB = Platform.OS === 'web';
const IS_WEB_WIDE = Platform.OS === 'web' && windowWidth >= 600;
const IS_DESKTOP_FORM = Platform.OS === 'web' && windowWidth >= 1024;
const IS_TABLET_FORM = Platform.OS === 'web' && windowWidth >= 600 && windowWidth < 1024;
const CustomDropdownThana = ({
    data = [],
    selectedValue,
    onValueChange,
    placeholder = 'থানা/এখতিয়ার নির্বাচন করুন',
    placeholderTextColor = '#989898',
    selectedTextColor = '#333333',
    containerStyle = {},
    dropdownButtonStyle = {},
    dropdownListStyle = {},
    itemStyle = {},
    itemTextStyle = {},
    searchPlaceholder = 'সার্চ করুন...',
    showSearch = null,
    maxHeight = 400,
    selectOptionsWidth = 280,
    onOpenChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [dropdownWidth, setDropdownWidth] = useState(selectOptionsWidth);
    const containerRef = useRef(null);

    const shouldShowSearch = showSearch !== null ? showSearch : data.length > 8;

    const filteredData = useMemo(() => {
        if (!searchText.trim()) return data;
        return data.filter(item =>
            item.label?.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [data, searchText]);

    const getSelectedLabel = () => {
        if (!selectedValue) return placeholder;

        const selected = data.find(
            item =>
                String(item.value) === String(selectedValue) ||
                String(item.id) === String(selectedValue)
        );

        if (!selected) return placeholder;

        return selected.label.length > 18
            ? `${selected.label.substring(0, 18)}...`
            : selected.label;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchText('');
                if (onOpenChange) {
                    onOpenChange(false);
                }
            }
        };

        if (Platform.OS === 'web') {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, onOpenChange]);

    const handleSelect = (item) => {
        if (!item) return;

        const itemValue = item.value;

        if (onValueChange) {
            onValueChange(itemValue);
            if (onOpenChange) {
                onOpenChange(false);
            }
        }

        setIsOpen(false);
        setSearchText('');
    };

    const toggleDropdown = () => {
        const nextState = !isOpen;
        setIsOpen(nextState);
        if (!nextState) {
            setSearchText('');
        }
        if (onOpenChange) {
            onOpenChange(nextState);
        }
    };

    const renderItem = ({ item }) => (
        <Pressable
            onPress={() => handleSelect(item)}
            style={({ hovered }) => [
                styles.dropdownItem,
                itemStyle,
                String(selectedValue) === String(item.value) && styles.selectedItem,
                hovered && styles.hoverItem,
            ]}
        >
            {({ hovered }) => (
                <Text
                    style={[
                        styles.itemText,
                        itemTextStyle,
                        hovered && styles.hoverItemText,
                        String(selectedValue) === String(item.value) && styles.selectedItemText,
                    ]}
                    numberOfLines={3}
                    ellipsizeMode="tail"
                >
                    {item.label}
                </Text>
            )}
        </Pressable>
    );

    // Determine if a value is selected
    const isValueSelected = selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== '';

    return (
        <View
            ref={containerRef}
            style={[styles.container, containerStyle, { zIndex: isOpen ? 9999999 : 1 }]}
        >
            <TouchableOpacity
                style={[styles.dropdownButton, dropdownButtonStyle]}
                onPress={toggleDropdown}
                activeOpacity={0.7}
            >
                <Text
                    style={[
                        styles.selectedText,
                        {
                            color: isValueSelected ? selectedTextColor : placeholderTextColor
                        }
                    ]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                >
                    {getSelectedLabel()}
                </Text>
                <Ionicons
                    name={isOpen ? "chevron-up" : "chevron-down"}
                    size={18}
                    color="#555"
                />
            </TouchableOpacity>

            {/* Dropdown List */}
            {isOpen && (
                <View style={[
                    styles.dropdownList,
                    dropdownListStyle,
                    { minWidth: dropdownWidth, maxHeight }
                ]}>
                    {shouldShowSearch && (
                        <View style={styles.searchContainer}>
                            <TextInput
                                style={styles.searchInput}
                                placeholder={searchPlaceholder}
                                placeholderTextColor="#999"
                                value={searchText}
                                onChangeText={setSearchText}
                            />
                            {searchText.length > 0 && (
                                <TouchableOpacity
                                    style={styles.clearButton}
                                    onPress={() => setSearchText('')}
                                >
                                    <Text style={styles.clearButtonText}>✕</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    )}

                    <FlatList
                        contentContainerStyle={{ marginBottom: IS_WEB_WIDE ? 30 : 200, marginLeft: 10, marginRight: 10 }}
                        data={filteredData}
                        keyExtractor={(item, index) => `${item.value}-${index}`}
                        renderItem={renderItem}
                        ListEmptyComponent={() => (
                            <View style={styles.emptyContainer}>
                                <Text style={styles.emptyText}>কোনো ডাটা পাওয়া যায়নি</Text>
                            </View>
                        )}
                        showsVerticalScrollIndicator={true}
                        keyboardShouldPersistTaps="handled"
                    />
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        position: 'relative',
        zIndex: 1000,
        overflow: 'visible',
        marginHorizontal: 10
    },
    dropdownButton: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        backgroundColor: '#FFF',
        paddingHorizontal: 10,
        paddingVertical: 2,
        height: 27
    },
    selectedText: {
        fontSize: 14,
        fontFamily: 'SolaimanLipi',
        flex: 1,
        lineHeight: 18,
        paddingRight: 8,
        paddingVertical: 8,
        fontWeight: '500',
        color: "#000"

    },
    dropdownList: {
        position: 'absolute',
        top: '100%',
        left: -10,
        right: -10,
        marginTop: 5,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        overflow: 'hidden',
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 12,
        zIndex: 9999999,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
    },
    searchInput: {
        flex: 1,
        height: 42,
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#333',
    },
    clearButton: {
        padding: 6,
    },
    clearButtonText: {
        fontSize: 16,
        color: '#999',
    },
    dropdownItem: {
        justifyContent: 'center',
        padding: 5
    },
    itemText: {
        fontSize: 14,
        fontFamily: 'SolaimanLipi',
        color: '#000',
        // lineHeight: 18,
        fontWeight: "normal"
    },
    selectedItem: {
        backgroundColor: '#f0f7ff',
    },
    selectedItemText: {
        color: '#1e40af',
        fontWeight: '600',
    },
    hoverItem: {
        backgroundColor: '#007BFF',
    },
    hoverItemText: {
        color: '#FFFFFF',
    },
    emptyContainer: {
        padding: 5,
        alignItems: 'center',
    },
    emptyText: {
        fontSize: 12,
        fontFamily: 'SolaimanLipi',
        color: '#999',
    },
});

export default CustomDropdownThana;
